// ============================================================
// features/player/components/PlayerTrigger.tsx
// URL横に配置されるミニパネル。URLの種類と同時視聴オプションの
// 有無に応じてボタン構成を自動的に切り替える。
// ============================================================
// 呼び出し例:
//   <PlayerTrigger url={seg.href} threadId={threadId} postNo={postNumber} />
//
// 登録元は LineDisplay 内の外部リンクに限らず、
// post 添付動画など複数箇所から呼び出される。
// ============================================================

import { useEffect, useMemo } from "react";
import { MdFormatListBulleted, MdPictureInPicture } from "react-icons/md";
import { usePlayerAPI } from "../hooks/usePlayerAPI";
import { usePlayerStore } from "../stores/playerStore";
import { analyzeUrl } from "../utils/providerDetect";

// ----------------------------------------------------------
// Props
// ----------------------------------------------------------

interface PlayerTriggerProps {
  /** 対象URL */
  url: string;
  /**
   * スレッドID。新APIでは ULID(string) になったため string を受ける。
   * ストアのキーは元々 string なので変換しない。
   */
  threadId: string | number;
  /**
   * 元投稿のレス番号（プレイリスト並び順に使用）。
   * 新APIでは postNumber が string になったため、ソート時は数値変換する。
   */
  postNo?: string | number;
  /** モーダル等のサブビュー内表示。プレイリスト登録・チェックボックスを無効化 */
  isSubView?: boolean;
}

// ----------------------------------------------------------
// コンポーネント
// ----------------------------------------------------------

export const PlayerTrigger: React.FunctionComponent<PlayerTriggerProps> = ({
  url,
  threadId,
  postNo,
  isSubView,
}: PlayerTriggerProps) => {
  const {
    play,
    playLive,
    playChecked,
    registerMedia,
    unregisterMedia,
    toggleMediaChecked,
  } = usePlayerAPI();

  // URL を分析（プロバイダー判定・ライブ判定・同時視聴パラメータ抽出）
  const analysis = useMemo(() => analyzeUrl(url), [url]);
  const { provider, providerId, isLive, hasDouji } = analysis;
  const isPlayable = provider !== null && providerId !== null;

  // threadId は string|number で受け取るが、ストアのキーは string
  const threadIdStr = String(threadId);
  // postNo は string|number で受け取るが、ソート時に数値が必要なので変換
  // NaN になったら undefined にして registerMedia に渡らないようにする
  const postNoRaw = typeof postNo === "string" ? Number(postNo) : postNo;
  const postNoNum =
    postNoRaw != null && Number.isFinite(postNoRaw) ? postNoRaw : undefined;

  // ---- チェック状態の取得 ----
  // フックは常に呼ぶ（Rules of Hooks）。非対応URLやライブの場合は false になるだけ
  const checked = usePlayerStore(
    (s) =>
      s.mediaListInThread[threadIdStr]?.find((m) => m.url === url)?.checked ??
      false,
  );

  // ---- スレッド内1個目のメディアかどうか ----
  // 連続再生ボタンは1個目のメディアにのみ表示する
  const isFirstMedia = usePlayerStore(
    (s) => s.mediaListInThread[threadIdStr]?.[0]?.url === url,
  );

  // ---- メディア登録簿への登録/解除 ----
  // ライブ配信はプレイリストに含めないので登録しない
  //
  // registerMedia / unregisterMedia はモジュールレベルの安定参照のため
  // 依存配列に含めても安全（参照が変わらない）。
  useEffect(() => {
    if (!isPlayable || isLive || !provider || !providerId || isSubView) return;
    registerMedia(threadIdStr, url, provider, providerId, postNoNum);
    return (): void => unregisterMedia(threadIdStr, url);
  }, [
    threadIdStr,
    url,
    provider,
    providerId,
    postNoNum,
    isLive,
    isPlayable,
    isSubView,
    registerMedia,
    unregisterMedia,
  ]);

  // 非対応URLなら何も描画しない（フックの後で判定）
  if (!isPlayable) return null;

  // ---- ボタン構成の決定 ----
  if (isLive) {
    return (
      <LiveTriggerPanel
        hasDouji={hasDouji}
        onPlayLive={(): void => playLive(url, threadIdStr)}
        onPlayDualSync={(): void =>
          playChecked(threadIdStr, { subMode: "sync_dual" })
        }
      />
    );
  }

  return (
    <VideoTriggerPanel
      hasDouji={hasDouji}
      checked={checked}
      isFirstMedia={isFirstMedia}
      isSubView={isSubView}
      onToggleChecked={(): void => toggleMediaChecked(threadIdStr, url)}
      onPlaySingle={(): void => play(url, threadIdStr)}
      onPlayPlaylist={(): void => playChecked(threadIdStr)}
      onPlaySingleSync={(): void => play(url, threadIdStr, { subMode: "sync" })}
      onPlayPlaylistSync={(): void =>
        playChecked(threadIdStr, { subMode: "sync" })
      }
    />
  );
};

// ----------------------------------------------------------
// 単品動画URL用パネル
// ----------------------------------------------------------

interface VideoTriggerPanelProps {
  hasDouji: boolean;
  checked: boolean;
  isFirstMedia: boolean;
  isSubView?: boolean;
  onToggleChecked: () => void;
  onPlaySingle: () => void;
  onPlayPlaylist: () => void;
  onPlaySingleSync: () => void;
  onPlayPlaylistSync: () => void;
}

const VideoTriggerPanel: React.FunctionComponent<VideoTriggerPanelProps> = ({
  hasDouji,
  checked,
  isFirstMedia,
  isSubView,
  onToggleChecked,
  onPlaySingle,
  onPlayPlaylist,
  onPlaySingleSync,
  onPlayPlaylistSync,
}: VideoTriggerPanelProps) => {
  return (
    <span className="inline-flex items-center gap-1 ml-1 align-middle">
      <TriggerButton onClick={onPlaySingle} title="再生">
        <MdPictureInPicture className="w-7 h-5 text-primary" /> 再生
      </TriggerButton>

      {!isSubView && isFirstMedia && (
        <TriggerButton onClick={onPlayPlaylist} title="連続再生:未実装">
          <MdFormatListBulleted className="w-7 h-5 text-primary" />{" "}
          連続再生:未実装
        </TriggerButton>
      )}

      {hasDouji && (
        <>
          <TriggerButton onClick={onPlaySingleSync} title="同時視聴">
            <MdPictureInPicture className="w-7 h-5 text-destructive" /> 同時視聴
          </TriggerButton>
          {!isSubView && isFirstMedia && (
            <TriggerButton
              onClick={onPlayPlaylistSync}
              title="連続再生+同時視聴:未実装"
            >
              <MdFormatListBulleted className="w-7 h-5 text-destructive" />{" "}
              連続再生同時視聴:未実装
            </TriggerButton>
          )}
        </>
      )}

      {!isSubView && (
        <label
          className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none"
          title="連続再生に含める（未実装）"
        >
          連続再生
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggleChecked}
            className="w-3.5 h-3.5 rounded border-border accent-primary cursor-pointer"
          />
        </label>
      )}
    </span>
  );
};

// ----------------------------------------------------------
// 配信URL用パネル
// ----------------------------------------------------------

interface LiveTriggerPanelProps {
  hasDouji: boolean;
  onPlayLive: () => void;
  onPlayDualSync: () => void;
}

const LiveTriggerPanel: React.FunctionComponent<LiveTriggerPanelProps> = ({
  hasDouji,
  onPlayLive,
  onPlayDualSync,
}: LiveTriggerPanelProps) => {
  return (
    <span className="inline-flex items-center gap-1 ml-1 align-middle">
      <TriggerButton onClick={onPlayLive} title="配信を再生">
        <MdPictureInPicture className="w-7 h-5 text-primary" /> Live
      </TriggerButton>

      {hasDouji && (
        <TriggerButton onClick={onPlayDualSync} title="連続再生+同時視聴+配信">
          <MdFormatListBulleted className="w-7 h-5 text-destructive" /> Live +
          動画同時視聴
        </TriggerButton>
      )}
    </span>
  );
};

// ----------------------------------------------------------
// 共通ボタン
// ----------------------------------------------------------

interface TriggerButtonProps {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}

const TriggerButton: React.FunctionComponent<TriggerButtonProps> = ({
  onClick,
  title,
  children,
}: TriggerButtonProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="inline-flex items-center h-9 min-w-9 justify-center px-2 py-0.5 text-18px leading-none rounded border border-border bg-card hover:bg-accent text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
    >
      {children}
    </button>
  );
};
